<template>
  <div class="app-signup-form-section">
    <div class="app-signup-form-section__main">
      <fieldset-email-parts
        v-model="inputData.email"
        :isReadOnly="true"
        :showError="false"
        :required="false"
      />

      <ValidationObserver slim ref="validationObserver" v-slot="{ errors, invalid }">
        <div class="app-signup-form-section__item">
          <ValidationProvider
            slim
            name="password"
            rules="required|passwordFormat"
            v-slot="{ errors }"
          >
            <fieldset-password-parts
              v-model="inputData.password"
              :required="true"
              :showError="errors.length > 0"
              :errorMessage="errors[0]"
            />
            <form-error-message-parts :showError="errors.length > 0" :message="errors[0]" />
          </ValidationProvider>
        </div>

        <div class="app-signup-form-section__item">
          <fieldset-name-parts
            :valueFamily="familyName"
            :valueFirst="firstName"
            :required="true"
            :showError="!!{ ...errors.familyName }[0] || !!{ ...errors.firstName }[0]"
            :errorMessage="{ ...errors.familyName }[0] || { ...errors.firstName }[0]"
            @inputfamilyName="inputValue($event, 'familyName')"
            @inputfirstName="inputValue($event, 'firstName')"
          />
        </div>

        <ValidationProvider slim name="birthDay" rules="required|validBirthDay" v-slot="{ errors }">
          <div class="app-signup-form-section__item">
            <fieldset-birthday-parts
              v-model="birthDay"
              :openBirthdayMenuId="openBirthdayMenuId"
              :required="true"
              :showError="errors.length > 0"
              :errorMessage="errors[0]"
              @input="inputValue($event, 'birthDay')"
              @onClickBirthday="handleBirthdayClicked"
              @onClickCloseBirthday="$emit('onClickCloseBirthday')"
            />
          </div>
        </ValidationProvider>

        <ValidationProvider slim name="country" rules="required" v-slot="{ errors }">
          <div class="app-signup-form-section__item">
            <fieldset-country-parts
              v-model="country"
              :required="true"
              :showError="errors.length > 0"
              :errorMessage="errors[0]"
              @input="inputValue($event, 'country')"
            />
          </div>
        </ValidationProvider>

        <div class="app-signup-form-section__term">
          <p v-html="$tc('SignupPage.FormInput.TermLinkText')"></p>
        </div>

        <div class="app-signup-form-section__agreement">
          <fieldset-checkbox-parts
            :value="inputData.agreement"
            :label="$tc('SignupPage.FormInput.Agreement')"
            @changed="onAgreementChecked"
          />
        </div>

        <action-buttons-section
          class="signup-form-section__action-buttons"
          scrollTargetSelector=".app-signup-form-section"
          :visible="true"
        >
          <atom-input-button
            :linkText="$tc('common.registration')"
            :isDisabled="invalid || submitDisabled"
            colorType="secondary"
            @push="onFormSubmit"
          />
        </action-buttons-section>
      </ValidationObserver>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref, watch } from '@vue/composition-api'
import FieldsetEmailParts from '@/components/common/form/FieldsetEmailParts.vue'
import FieldsetPasswordParts from '@/components/common/form/FieldsetPasswordParts.vue'
import FieldsetCheckboxParts from '@/components/common/form/FieldsetCheckboxParts.vue'
import ActionButtonsSection from '@/components/common/form/ActionButtonsSection.vue'
import AtomInputButton from '@/components/atoms/input/AtomInputButton.vue'
import FieldsetNameParts from '@/components/common/form/FieldsetNameParts.vue'
import FieldsetCountryParts from '@/components/common/form/FieldsetCountryParts.vue'
import FieldsetBirthdayParts from '@/components/common/form/FieldsetBirthdayParts.vue'
import type { EditUserType } from '@/store/stores/pageStore/MypagePage/MypagePageStore'
import type { OpenBirthdayMenuIdType } from '@/components/common/form/FieldsetBirthdayParts.vue'
import FormErrorMessageParts from '@/components/common/form/FormErrorMessageParts.vue'

/**
 * 会員登録で必要な入力項目
 */
type RegistrationInputDataType = {
  email: string
  password: string
  agreement: boolean
}

/**
 * 会員情報入力項目のプロパティ
 */
type UserInputPropertyType = 'familyName' | 'firstName' | 'birthDay' | 'country'

/**
 * アプリ新規会員登録 会員情報入力セクション
 */
export default defineComponent({
  name: 'SignupFormSection',
  components: {
    FormErrorMessageParts,
    FieldsetBirthdayParts,
    FieldsetCountryParts,
    FieldsetNameParts,
    FieldsetEmailParts,
    FieldsetPasswordParts,
    FieldsetCheckboxParts,
    ActionButtonsSection,
    AtomInputButton,
  },
  props: {
    /**
     * 無料会員登録処理実行中かどうか
     */
    isRegistering: {
      type: Boolean,
      required: true,
    },
    /**
     * 認証メール送信画面で入力したメールアドレス
     */
    email: {
      type: String,
      required: true,
    },
    /**
     * 入力対象の会員情報
     */
    userInfo: {
      type: Object as PropType<EditUserType>,
      required: true,
    },
    /**
     * 年・月・日のうちどれを変更するか
     */
    openBirthdayMenuId: {
      type: String as PropType<OpenBirthdayMenuIdType>,
      default: '',
    },
  },
  setup(props, { emit }) {
    const inputData = ref<RegistrationInputDataType>({
      email: '',
      password: '',
      agreement: false,
    })

    /** 以下の場合は「登録する」ボタンを押せない
     * 1. 規約未同意
     * 2. 無料会員登録処理実行中
     */
    const submitDisabled = computed(() => !inputData.value.agreement || props.isRegistering)

    const onAgreementChecked = (agreementValue: boolean) => {
      inputData.value.agreement = agreementValue
    }

    const handleBirthdayClicked = (menuId: string) => {
      emit('onClickBirthday', menuId)
    }

    watch(
      () => props.email,
      () => {
        inputData.value.email = props.email
      },
    )

    return {
      // SignupFormSection
      inputData,
      submitDisabled,
      onAgreementChecked,
      handleBirthdayClicked,
      // 入力する会員情報
      firstName: props.userInfo.firstName ?? '',
      familyName: props.userInfo.familyName ?? '',
      birthDay: props.userInfo.birthDay,
      country: props.userInfo.country || 'JP',
    }
  },
  methods: {
    /**
     * 入力対象の会員情報
     */
    editUserData(): EditUserType {
      return {
        firstName: this.firstName,
        familyName: this.familyName,
        firstNameKana: '',
        familyNameKana: '',
        userDisplayName: '',
        birthDay: this.birthDay,
        gender: '',
        country: this.country,
        prefecture: '',
        zipcode: '',
      }
    },
    /**
     * 入力対象が変更された場合
     */
    inputValue(value: string, property: UserInputPropertyType) {
      this[property] = value
      this.onSetEditUserInfo()
    },
    onSetEditUserInfo() {
      /**
       * 入力対象が変更されたことを通知する
       * @event onSetEditUserInfo
       */
      this.$emit('onChangeEditUserInfo', this.editUserData())
    },
    onFormSubmit() {
      /**
       * ボタンが押下されたことを通知する
       */
      this.$emit('formSubmit', this.inputData.password, this.userInfo)
    },
  },
})
</script>

<style lang="scss" scoped>
.app-signup-form-section {
  padding: 24px 16px 0;

  &__form {
    width: 100%;
    max-width: 768px;
    margin: 0 auto;
  }

  &__term_message {
    margin: 24px 0 16px;
    color: $gray75;
    font-size: 12px;
    line-height: 2;
  }

  @media screen and (min-width: $responsiveTabletWidth) {
    padding: 48px 16px;

    &__term_message {
      line-height: 1.67;
    }
  }
}
</style>

<style scoped lang="scss">
.app-signup-form-section {
  height: calc(100svh - 48px);
  color: $gray75;
  background-color: $gray1;

  &__main {
    max-width: 480px;
    height: calc(100% - 44px);
    padding: 24px 16px 102px;
    margin: 0 auto;
    overflow-y: auto;
  }

  &__item {
    margin-top: 24px;
  }

  &__term {
    margin-top: 24px;
    font-size: 12px;
    line-height: 1.67;
    text-align: center;

    ::v-deep {
      a {
        font-weight: 700;
        text-decoration: underline;
      }
    }
  }

  &__agreement {
    margin-top: 16px;
    text-align: center;
  }

  ::v-deep {
    .action-buttons-section {
      bottom: env(safe-area-inset-bottom);
    }
  }
}
</style>
